/**
 * AuthShell.tsx  -  Shared frame for sign-in and sign-up: brand panel on the
 * left (desktop only), the form on the right.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import LitlingsMark from "@/components/LitlingsMark";

export function AuthShell({
  title,
  subtitle,
  tagline = "Teaching reading, made brighter.",
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  tagline?: string;
  children: ReactNode;
}) {
  return (
    <div className="grid min-h-screen bg-white lg:grid-cols-2">
      <aside className="relative hidden overflow-hidden bg-brand-blue p-12 text-white lg:flex lg:flex-col lg:justify-between">
        <div
          aria-hidden="true"
          className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-brand-yellow"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-16 -left-10 h-56 w-56 rotate-[18deg] bg-brand-red"
        />
        <Link to="/" className="relative flex items-center gap-2.5">
          <LitlingsMark size={32} />
          <span className="font-display text-xl font-extrabold">Litlings</span>
        </Link>
        <p className="relative max-w-md font-display text-4xl font-extrabold leading-[1.08] tracking-[-0.03em]">
          {tagline}
        </p>
        <p className="relative text-sm text-white/80">
          Phonics teaching, marking and tracking for primary schools and tutors.
        </p>
      </aside>

      <main
        id="main-content"
        className="flex flex-col items-center justify-center px-5 py-12"
      >
        <div className="w-full max-w-sm">
          <Link to="/" className="mb-8 flex items-center gap-2.5 lg:hidden">
            <LitlingsMark size={30} />
            <span className="font-display text-xl font-extrabold">
              Litlings
            </span>
          </Link>
          <h1 className="font-display text-3xl font-extrabold tracking-[-0.025em]">
            {title}
          </h1>
          {subtitle && <p className="mt-1.5 text-portal-muted">{subtitle}</p>}
          <div className="mt-8">{children}</div>
        </div>
      </main>
    </div>
  );
}
